import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { toast } from 'react-toastify';

import Images from '../utils/Images';
import Menu from './Menu';
import {
    BoltRounded,
    CameraAltRounded,
    CloseRounded,
    CoffeeRounded,
    QrCode,
    SecurityRounded,
} from '@mui/icons-material';
import SplashScreenLoading from '../components/SplashScreenLoading';
import { clearLocalCart } from '../components/CartDrawer';

const SPLASH_DURATION_MS = 4000;
const MONGO_ID_RE = /^[a-fA-F0-9]{24}$/;
const splashCompletedOutlets = new Set();

const extractOutletIdFromPath = (pathLike) => {
    const segments = String(pathLike || '')
        .split(/[/?#]/)
        .filter(Boolean);

    if (segments.length === 0) {
        return null;
    }

    const menuIndex = segments.findIndex(
        (segment) => segment.toLowerCase() === 'menu'
    );

    if (menuIndex >= 0 && segments[menuIndex + 1]) {
        return segments[menuIndex + 1];
    }

    const last = segments[segments.length - 1];
    return last || null;
};

const extractOutletIdFromScan = (rawValue) => {
    const value = String(rawValue || '').trim();

    if (!value) {
        return null;
    }

    if (MONGO_ID_RE.test(value)) {
        return value;
    }

    try {
        const url = new URL(value);
        const fromQuery =
            url.searchParams.get('outletId') ||
            url.searchParams.get('outlet');

        if (fromQuery) {
            return fromQuery;
        }

        const fromHash = extractOutletIdFromPath(
            url.hash.replace(/^#/, '')
        );

        if (fromHash) {
            return fromHash;
        }

        return extractOutletIdFromPath(url.pathname);
    } catch {
        return extractOutletIdFromPath(value);
    }
};

const isIgnorableScanError = (error) => {
    if (!error) {
        return true;
    }

    const name = error?.name || '';
    const message = error?.message || '';

    return (
        name === 'NotFoundException' ||
        name === 'ChecksumException' ||
        name === 'FormatException' ||
        message.includes('No MultiFormat Readers')
    );
};

const SplashScreen = () => {
    const { outletId } = useParams();
    const navigate = useNavigate();

    if (outletId) {
        const previousOutletId = localStorage.getItem('outletId');

        if (previousOutletId && previousOutletId !== outletId) {
            clearLocalCart();
            splashCompletedOutlets.delete(previousOutletId);
        }
    }

    const [ready, setReady] = useState(() =>
        Boolean(outletId && splashCompletedOutlets.has(outletId))
    );
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scannerActive, setScannerActive] = useState(false);
    const [cameraError, setCameraError] = useState('');
    const scanHandledRef = useRef(false);
    const invalidToastAtRef = useRef(0);
    const videoRef = useRef(null);
    const controlsRef = useRef(null);

    const stopScanner = useCallback(() => {
        try {
            controlsRef.current?.stop();
        } catch {
            // ignore stop errors while tearing down
        }
        controlsRef.current = null;

        const mediaStream = videoRef.current?.srcObject;
        if (mediaStream) {
            mediaStream
                .getTracks()
                .forEach((track) => track.stop());
            videoRef.current.srcObject = null;
        }
    }, []);

    const handleOpenCamera = () => {
        scanHandledRef.current = false;
        invalidToastAtRef.current = 0;
        setCameraError('');
        setScannerActive(false);
        setScannerOpen(true);
    };

    const handleCloseScanner = useCallback(() => {
        stopScanner();
        setScannerActive(false);
        setScannerOpen(false);
        setCameraError('');
    }, [stopScanner]);

    const handleDecodedText = useCallback(
        (scannedText) => {
            if (scanHandledRef.current) {
                return;
            }

            const scannedOutletId =
                extractOutletIdFromScan(scannedText);

            if (!scannedOutletId) {
                const now = Date.now();
                if (now - invalidToastAtRef.current > 2500) {
                    invalidToastAtRef.current = now;
                    toast.error(
                        'Invalid QR code. Please scan an outlet QR code.'
                    );
                }
                return;
            }

            scanHandledRef.current = true;
            stopScanner();
            setScannerActive(false);
            setScannerOpen(false);
            navigate(`/${scannedOutletId}`);
        },
        [navigate, stopScanner]
    );

    useEffect(() => {
        if (!scannerOpen || !scannerActive) {
            return undefined;
        }

        let cancelled = false;
        const reader = new BrowserMultiFormatReader();

        const startScanner = async () => {
            if (!videoRef.current) {
                return;
            }

            try {
                const controls =
                    await reader.decodeFromConstraints(
                        {
                            video: {
                                facingMode: {
                                    ideal: 'environment',
                                },
                            },
                        },
                        videoRef.current,
                        (result, error) => {
                            if (cancelled || scanHandledRef.current) {
                                return;
                            }

                            if (result) {
                                const text =
                                    result.getText?.() ||
                                    result.text ||
                                    '';

                                if (text) {
                                    handleDecodedText(text);
                                }
                                return;
                            }

                            if (isIgnorableScanError(error)) {
                                return;
                            }

                            const message =
                                error?.message || '';

                            if (
                                message.includes('NotAllowed') ||
                                message.includes('Permission') ||
                                error?.name === 'NotAllowedError'
                            ) {
                                setCameraError(
                                    'Camera access was denied. Please allow camera permission in your browser settings.'
                                );
                            } else if (
                                error?.name === 'NotFoundError' ||
                                message.includes('DevicesNotFound')
                            ) {
                                setCameraError(
                                    'No camera found on this device.'
                                );
                            }
                        }
                    );

                if (cancelled) {
                    controls?.stop();
                    return;
                }

                controlsRef.current = controls;
            } catch (error) {
                if (cancelled) {
                    return;
                }

                const message = error?.message || '';

                if (
                    message.includes('NotAllowed') ||
                    message.includes('Permission') ||
                    error?.name === 'NotAllowedError'
                ) {
                    setCameraError(
                        'Camera access was denied. Please allow camera permission in your browser settings.'
                    );
                } else if (
                    error?.name === 'NotFoundError' ||
                    message.includes('DevicesNotFound')
                ) {
                    setCameraError(
                        'No camera found on this device.'
                    );
                } else {
                    setCameraError(
                        'Unable to start the camera. Please try again.'
                    );
                }
            }
        };

        startScanner();

        return () => {
            cancelled = true;
            stopScanner();
        };
    }, [scannerOpen, scannerActive, handleDecodedText, stopScanner]);

    useEffect(() => {
        // No outlet ID means user has not entered through
        // the outlet QR code.
        if (!outletId) {
            setReady(false);
            return undefined;
        }

        const previousOutletId =
            localStorage.getItem('outletId');

        if (
            previousOutletId &&
            previousOutletId !== outletId
        ) {
            clearLocalCart();
            splashCompletedOutlets.delete(previousOutletId);
        }

        localStorage.setItem(
            'outletId',
            outletId
        );

        try {
            const stored =
                JSON.parse(
                    localStorage.getItem('outletData')
                ) || null;

            if (stored?._id !== outletId) {
                localStorage.setItem(
                    'outletData',
                    JSON.stringify({
                        _id: outletId,
                    })
                );
            }
        } catch {
            localStorage.setItem(
                'outletData',
                JSON.stringify({
                    _id: outletId,
                })
            );
        }

        if (splashCompletedOutlets.has(outletId)) {
            setReady(true);
            return undefined;
        }

        setReady(false);

        const timer =
            window.setTimeout(() => {
                splashCompletedOutlets.add(outletId);
                setReady(true);
            }, SPLASH_DURATION_MS);

        return () =>
            window.clearTimeout(timer);
    }, [outletId]);

    /*
     * --------------------------------------------------
     * OUTLET SPLASH + MENU PREFETCH
     * Menu mounts immediately so product/ad APIs
     * load during the splash animation.
     * --------------------------------------------------
     */
    if (outletId) {
        return (
            <>
                <Menu splashComplete={ready} />

                {!ready && (
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 9999,
                        }}
                    >
                        <SplashScreenLoading />
                    </Box>
                )}
            </>
        );
    }

    /*
     * --------------------------------------------------
     * NO OUTLET ID
     * Scan QR Screen
     * --------------------------------------------------
     */
    if (!outletId) {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: '100vh',
                    minHeight: '100dvh',

                    display: 'flex',
                    justifyContent: 'center',

                    background:
                        'linear-gradient(180deg, #003456 0%, #004D74 38%, #007EAD 100%)',

                    overflow: 'hidden',

                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 430,
                        minHeight: '100dvh',

                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',

                        position: 'relative',

                        boxSizing: 'border-box',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 38,
                            left: 25,

                            width: 42,
                            height: 42,

                            border:
                                '1px solid rgba(0, 151, 210, 0.18)',

                            transform: 'rotate(45deg)',

                            pointerEvents: 'none',
                        }}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            top: 65,
                            right: 24,

                            width: 45,
                            height: 45,

                            opacity: 0.12,

                            backgroundImage:
                                'radial-gradient(#35B9ED 2px, transparent 2px)',

                            backgroundSize:
                                '10px 10px',

                            pointerEvents: 'none',
                        }}
                    />

                    {/* Xpanse Logo */}
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: -10,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: 0.5,
                        }}
                    >
                        <Box
                            component="img"
                            src={Images.xpanse4}
                            alt="Xpanse"
                            sx={{
                                display: 'block',

                                width: {
                                    xs: 120,
                                    sm: 135,
                                },

                                height: 70,

                                objectFit: 'contain',

                                filter:
                                    'drop-shadow(0 8px 18px rgba(0,0,0,0.15))',
                            }}
                        />
                    </motion.div>

                    {/* Heading */}
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 12,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: 0.5,
                            delay: 0.1,
                        }}
                        style={{
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: 'center',
                                px: 2,
                            }}
                        >
                            <Typography
                                component="div"
                                sx={{
                                    color: '#FFFFFF',

                                    fontSize: {
                                        xs: 42,
                                        sm: 48,
                                    },

                                    fontWeight: 800,
                                    lineHeight: 1.05,

                                    letterSpacing:
                                        '-1.5px',
                                }}
                            >
                                Scan{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        background:
                                            'linear-gradient(180deg, #42CBFF 0%, #00A8EA 100%)',

                                        WebkitBackgroundClip:
                                            'text',

                                        WebkitTextFillColor:
                                            'transparent',

                                        backgroundClip:
                                            'text',
                                    }}
                                >
                                    QR
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.7,

                                    color:
                                        'rgba(255,255,255,0.94)',

                                    fontSize: {
                                        xs: 30,
                                        sm: 22,
                                    },

                                    fontWeight: 400,

                                    lineHeight: 1.3,
                                }}
                            >
                                to place your order
                            </Typography>
                        </Box>
                    </motion.div>

                    {/* Bird */}
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 20,
                            scale: 0.95,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            scale: 1,
                        }}
                        transition={{
                            duration: 0.6,
                            delay: 0.15,
                            ease: [
                                0.22,
                                1,
                                0.36,
                                1,
                            ],
                        }}
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '10px',
                            position: 'relative',
                            zIndex: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={Images.scanBird}
                            alt=""
                            sx={{
                                display: 'block',

                                width: {
                                    xs: 190,
                                    sm: 215,
                                },

                                maxWidth: '58vw',

                                height: 'auto',

                                objectFit: 'contain',

                                mb: '-15px',
                                filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.08))',
                            }}
                        />
                    </motion.div>

                    <Box
                        sx={{
                            width: '100%',
                            flex: 1,

                            position: 'relative',
                            zIndex: 2,

                            background:
                                'linear-gradient(180deg, #FFFFFF 0%, #F8FCFF 100%)',

                            borderRadius:
                                '108px 108px 0 0',

                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',

                            px: {
                                xs: 3,
                                sm: 4,
                            },

                            pt: 5,
                            pb:
                                'max(24px, env(safe-area-inset-bottom))',

                            boxSizing: 'border-box',

                            boxShadow:
                                '0 -8px 30px rgba(0, 48, 73, 0.08)',
                        }}
                    >
                        {/* QR Scan Icon */}
                        <Box
                            sx={{
                                width: 150,
                                height: 150,

                                position: 'relative',

                                display: 'flex',
                                justifyContent:
                                    'center',
                                alignItems: 'center',

                                mt: 0.5,
                            }}
                        >
                            {/* Top Left */}
                            <Box
                                sx={{
                                    position:
                                        'absolute',
                                    top: 0,
                                    left: 0,

                                    width: 34,
                                    height: 34,

                                    borderTop:
                                        '4px solid #0798D2',

                                    borderLeft:
                                        '4px solid #0798D2',

                                    borderRadius:
                                        '10px 0 0 0',
                                }}
                            />

                            {/* Top Right */}
                            <Box
                                sx={{
                                    position:
                                        'absolute',
                                    top: 0,
                                    right: 0,

                                    width: 34,
                                    height: 34,

                                    borderTop:
                                        '4px solid #0798D2',

                                    borderRight:
                                        '4px solid #0798D2',

                                    borderRadius:
                                        '0 10px 0 0',
                                }}
                            />

                            {/* Bottom Left */}
                            <Box
                                sx={{
                                    position:
                                        'absolute',
                                    bottom: 0,
                                    left: 0,

                                    width: 34,
                                    height: 34,

                                    borderBottom:
                                        '4px solid #0798D2',

                                    borderLeft:
                                        '4px solid #0798D2',

                                    borderRadius:
                                        '0 0 0 10px',
                                }}
                            />

                            {/* Bottom Right */}
                            <Box
                                sx={{
                                    position:
                                        'absolute',
                                    bottom: 0,
                                    right: 0,

                                    width: 34,
                                    height: 34,

                                    borderBottom:
                                        '4px solid #0798D2',

                                    borderRight:
                                        '4px solid #0798D2',

                                    borderRadius:
                                        '0 0 10px 0',
                                }}
                            />

                            <QrCode sx={{
                                    width: 102,
                                    height: 102,
                                    color: '#0798D2',
                                    opacity: 0.35,
                                    backgroundSize:
                                        '18px 18px',
                                }} />
                            {/* Fake QR graphic */}
                            {/* <Box
                                sx={{
                                    width: 72,
                                    height: 72,

                                    opacity: 0.35,

                                    backgroundImage: `
                                        linear-gradient(90deg, #0798D2 25%, transparent 25%),
                                        linear-gradient(#0798D2 25%, transparent 25%)
                                    `,

                                    backgroundSize:
                                        '18px 18px',

                                    border:
                                        '7px solid rgba(7,152,210,0.25)',

                                    boxSizing:
                                        'border-box',
                                }}
                            /> */}
                        </Box>

                        {/* Instructions */}
                        <Typography
                            sx={{
                                mt: 4,

                                maxWidth: 300,

                                color: '#08375A',

                                fontSize: {
                                    xs: 17,
                                    sm: 18,
                                },

                                lineHeight: 1.4,

                                fontWeight: 500,

                                textAlign: 'center',
                            }}
                        >
                            Point your camera at the
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 310,

                                color: '#08375A',

                                fontSize: {
                                    xs: 17,
                                    sm: 18,
                                },

                                lineHeight: 1.4,

                                fontWeight: 500,

                                textAlign: 'center',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    color: '#078ED0',
                                    fontWeight: 800,
                                }}
                            >
                                outlet QR code
                            </Box>{' '}
                            to get started
                        </Typography>

                        {/* Benefits */}
                        <Box
                            sx={{
                                width: '100%',

                                display: 'flex',
                                justifyContent:
                                    'space-between',

                                alignItems:
                                    'flex-start',

                                mt: 5,

                                px: {
                                    xs: 0,
                                    sm: 1,
                                },
                            }}
                        >
                            <Benefit
                                icon={<BoltRounded />}
                                label="Quick Order"
                            />

                            <Benefit
                                icon={<SecurityRounded />}
                                label="Easy & Secure"
                            />

                            <Benefit
                                icon={<CoffeeRounded />}
                                label="Fresh & Fast"
                            />
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<CameraAltRounded />}
                            onClick={handleOpenCamera}
                            sx={{
                                mt: 4,
                                height: 56,
                                borderRadius: '18px',
                                textTransform: 'none',
                                fontSize: 17,
                                fontWeight: 700,
                                background:
                                    'linear-gradient(90deg, #078ED0 0%, #16B5E8 100%)',
                                boxShadow:
                                    '0 10px 24px rgba(7,142,208,0.28)',
                            }}
                        >
                            Scan QR Code
                        </Button>
                    </Box>
                </Box>

                <Dialog
                    fullScreen
                    open={scannerOpen}
                    onClose={handleCloseScanner}
                    TransitionProps={{
                        onEntered: () =>
                            setScannerActive(true),
                        onExit: () => {
                            setScannerActive(false);
                            stopScanner();
                        },
                    }}
                    PaperProps={{
                        sx: {
                            background:
                                'linear-gradient(180deg, #003456 0%, #004D74 100%)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            minHeight: '100dvh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            pt: 'max(16px, env(safe-area-inset-top))',
                            pb: 'max(24px, env(safe-area-inset-bottom))',
                            px: 2,
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 430,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontSize: 20,
                                    fontWeight: 700,
                                }}
                            >
                                Scan QR Code
                            </Typography>

                            <IconButton
                                onClick={
                                    handleCloseScanner
                                }
                                aria-label="Close scanner"
                                sx={{
                                    color: '#fff',
                                    background:
                                        'rgba(255,255,255,0.12)',
                                }}
                            >
                                <CloseRounded />
                            </IconButton>
                        </Box>

                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: 15,
                                fontWeight: 500,
                                textAlign: 'center',
                                mb: 3,
                                maxWidth: 320,
                            }}
                        >
                            Align the outlet QR code
                            within the frame
                        </Typography>

                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 320,
                                aspectRatio: '1',
                                position: 'relative',
                                borderRadius: '20px',
                                overflow: 'hidden',
                                background: '#000',
                                boxShadow:
                                    '0 16px 40px rgba(0,0,0,0.35)',
                            }}
                        >
                            <video
                                ref={videoRef}
                                muted
                                playsInline
                                autoPlay
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    background: '#000',
                                }}
                            />

                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    pointerEvents: 'none',
                                    border: '2px solid rgba(66,203,255,0.7)',
                                    borderRadius: '20px',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        left: 16,
                                        width: 32,
                                        height: 32,
                                        borderTop:
                                            '4px solid #42CBFF',
                                        borderLeft:
                                            '4px solid #42CBFF',
                                        borderRadius:
                                            '8px 0 0 0',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        width: 32,
                                        height: 32,
                                        borderTop:
                                            '4px solid #42CBFF',
                                        borderRight:
                                            '4px solid #42CBFF',
                                        borderRadius:
                                            '0 8px 0 0',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        left: 16,
                                        width: 32,
                                        height: 32,
                                        borderBottom:
                                            '4px solid #42CBFF',
                                        borderLeft:
                                            '4px solid #42CBFF',
                                        borderRadius:
                                            '0 0 0 8px',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                        width: 32,
                                        height: 32,
                                        borderBottom:
                                            '4px solid #42CBFF',
                                        borderRight:
                                            '4px solid #42CBFF',
                                        borderRadius:
                                            '0 0 8px 0',
                                    }}
                                />
                            </Box>
                        </Box>

                        {cameraError ? (
                            <Typography
                                sx={{
                                    mt: 3,
                                    px: 2,
                                    color: '#FFB4B4',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    maxWidth: 320,
                                }}
                            >
                                {cameraError}
                            </Typography>
                        ) : (
                            <Typography
                                sx={{
                                    mt: 3,
                                    color: 'rgba(255,255,255,0.75)',
                                    fontSize: 13,
                                    textAlign: 'center',
                                }}
                            >
                                Hold steady — scanning
                                automatically
                            </Typography>
                        )}
                    </Box>
                </Dialog>
            </Box>
        );
    }

};


/*
 * --------------------------------------------------
 * Benefit Item
 * --------------------------------------------------
 */
const Benefit = ({
    icon,
    label,
}) => (
    <Box
        sx={{
            width: '31%',

            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',

            textAlign: 'center',
        }}
    >
        <Box
            sx={{
                width: 42,
                height: 42,

                borderRadius: '50%',

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

                background:
                    'linear-gradient(180deg, #E8F7FD 0%, #DDF3FC 100%)',

                color: '#1E9CED' ,
                fontSize: 21,

                mb: 1,
            }}
        >
            {icon}
        </Box>

        <Typography
            sx={{
                color: '#0E4961',

                fontSize: {
                    xs: 10.5,
                    sm: 11.5,
                },

                lineHeight: 1.25,

                fontWeight: 700,

                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Typography>
    </Box>
);

export default SplashScreen;